import React, { useState } from 'react';
import { CheckCircle, AlertCircle, Shield, Users, UserCheck, Crown } from 'lucide-react';

const AssessmentPanel = ({ onAssessmentSubmission, ticket }) => {
  const [assessmentData, setAssessmentData] = useState({
    businessActivities: ticket?.ticket_affected || '',
    possibleImpact: ticket?.ticket_impacted || '',
    mitigationControl: ticket?.ticket_mitigation || '',
    recommended: ticket?.ticket_recommended === 1
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onAssessmentSubmission(assessmentData);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setAssessmentData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="bg-yellow-500 text-white px-6 py-4 rounded-t-lg">
        <div className="flex items-center">
          <Shield className="w-6 h-6 mr-3" />
          <h2 className="text-xl font-semibold">Request Assessment</h2>
        </div>
      </div>
      
      <div className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Assessment Fields */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Activities Affected
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={5}
                value={assessmentData.businessActivities}
                onChange={(e) => handleInputChange('businessActivities', e.target.value)}
                placeholder="Describe the business activities that will be affected by this access request..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Possible Impact
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={5}
                value={assessmentData.possibleImpact}
                onChange={(e) => handleInputChange('possibleImpact', e.target.value)}
                placeholder="Describe the possible impact on business operations..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mitigation Control
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={5}
                value={assessmentData.mitigationControl}
                onChange={(e) => handleInputChange('mitigationControl', e.target.value)}
                placeholder="Describe the mitigation controls and security measures..."
                required
              />
            </div>
          </div>

          {/* Recommendation */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                checked={assessmentData.recommended}
                onChange={(e) => handleInputChange('recommended', e.target.checked)}
              />
              <span className="ml-2 text-sm font-medium text-gray-700">
                Recommend this access request for approval
              </span>
            </label>
            {!assessmentData.recommended && (
              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-center">
                  <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                  <span className="text-red-800 text-sm">
                    If not recommended, the request will be rejected and no approval workflow will be initiated.
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Submit Assessment
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssessmentPanel;
